import { I18n } from '@aws-amplify/core';
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components';
import { Auth } from 'aws-amplify';
import { AxiosResponse } from 'axios';
import { routerActions } from 'connected-react-router';
import React, { lazy, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import './App.scss';
import { COOKIE_DOMAIN, IS_DEV, LEGACY_COOKIE_DOMAIN } from './configs';
import { useAppDispatch, useAppSelector } from './hooks';
import { COGNITO_TRANSLATIONS } from './i18';
import { Shell } from './pages/shell';
import { isLoading, stopLoading } from './slice/loadingSlice';
import { CognitoPayload, signIn, signOutAsync } from './slice/userSlice';
import { format } from 'date-fns';
import Spinner from './components/Spinner';
import { ErrorBoundaryWrapper, ErrorPage } from './pages/error';
import { ERROR_CODE } from './constant/errorCode';
import { ERROR_MESSAGE } from './constant/message';
import RouteWrapper from './components/RouteWrapper/RouteWrapper';
import { cookieUtils } from './utils/storage/cookieUtils';
import { serverApi, requestServerApi } from './api/api';
const Top = lazy(() => import('./pages/top/top'));
const Signin = lazy(() => import('./pages/signin/signin'));
const Learners = lazy(() => import('./pages/learners/index'));
const Learner = lazy(() => import('./pages/learner/index'));
const Lessons = lazy(() => import('./pages/lessons/index'));
const Lesson = lazy(() => import('./pages/lesson/index'));
const Notices = lazy(() => import('./pages/notices/notices'));
const Agreement = lazy(() => import('./pages/payments_company/index'));
const Contact = lazy(() => import('./pages/contact/index'));
const LessonNotes = lazy(() => import('./pages/lesson_notes/lesson_notes'));
const Feedback = lazy(() => import('./pages/feedback/feedback'));

I18n.putVocabulariesForLanguage('ja', COGNITO_TRANSLATIONS);
const PrivateRoute = ({ render, userState, ...rest }: any) => {
  return (
    <Route
      {...rest}
      render={
        userState.authState === AuthState.SignedIn && userState.user
          ? render
          : () => <Redirect to="/signin" />
      }
    />
  );
};
export const Loading = () => <Spinner />;

const extractCognitoPayload = (authenticatedUser: any): CognitoPayload => {
  try {
    return {
      'cognito:username': authenticatedUser.signInUserSession.idToken.payload['cognito:username'],
      'cognito:companyId': authenticatedUser.attributes['custom:companyId'],
      email: authenticatedUser.signInUserSession.idToken.payload.email,
      exp: authenticatedUser.signInUserSession.idToken.payload.exp,
    };
  } catch (error) {
    throw error;
  }
};

// アクセストークンがまだ有効でも無条件でCognitoにリフレッシュを要求し、
// 成功時に新ドメインへ完全なCookieセット（idToken/accessToken/refreshToken等）を
// 書き込ませる。書き込みが確定したことをコールバックの成功で確認できたタイミングでのみ、
// 旧ドメイン(company.*)の重複Cookieを削除する
// Record log when company user is active
const pingCompanyUserActiveLog = (userId: string) => {
  if (!userId) return;
  requestServerApi(() => serverApi.organizationsControllerCreateActiveCompanyUserLog(userId)).catch(
    (e) => console.log('pingCompanyUserActiveLog error: ', e),
  );
};

const forceRefreshToNewDomainAndCleanupLegacyCookies = (authenticatedUser: any) => {
  const session = authenticatedUser.getSignInUserSession?.();
  const refreshToken = session?.getRefreshToken?.();
  if (!refreshToken) return;
  authenticatedUser.refreshSession(refreshToken, (err: unknown) => {
    if (err) return;
    cookieUtils.removeCookiesOnLegacyDomain('CognitoIdentityServiceProvider', LEGACY_COOKIE_DOMAIN);
  });
};

const App = () => {
  const userState = useAppSelector((state) => state.user);
  const isloading = useAppSelector(isLoading);
  const dispatch = useAppDispatch();
  useEffect(() => {
    const init = async () => {
      const authenticatedUser = await Auth.currentAuthenticatedUser().catch((e) => null);
      if (authenticatedUser) {
        const expayload = extractCognitoPayload(authenticatedUser);
        dispatch(
          signIn({
            authState: AuthState.SignedIn,
            user: expayload,
          }),
        );
        pingCompanyUserActiveLog(expayload['cognito:username']);
        dispatch(stopLoading());
        if (!IS_DEV) {
          forceRefreshToNewDomainAndCleanupLegacyCookies(authenticatedUser);
        }
      } else {
        // 有効な認証情報が復元できなかった場合。旧ドメイン(company.*)のCookieが
        // 残っていると次回以降のログインにも影響するため、このタイミングで
        // 新旧両方のドメインのCognito Cookieを削除しておく
        cookieUtils.removeCookiesOnLegacyDomain('CognitoIdentityServiceProvider', LEGACY_COOKIE_DOMAIN);
        cookieUtils.removeCookiesOnLegacyDomain('CognitoIdentityServiceProvider', COOKIE_DOMAIN);
        await dispatch(signOutAsync());
        dispatch(stopLoading());
        dispatch(routerActions.push('/signin'));
      }
    };
    init();
    return onAuthUIStateChange((nextAuthState, authenticatedUser) => {
      if (nextAuthState === AuthState.SignedIn && authenticatedUser) {
        // サインイン成功時は必ず新ドメインへ完全なトークン一式が書き込まれるため、
        // このタイミングでのみ旧ドメインの重複Cookieを削除して問題ない。
        // js-cookieのgetは同名Cookieのうち先に出現した方（＝旧ドメインの古いCookie）を
        // 返してしまうため、削除せず放置すると新ドメインへ書き込んだ値が読まれなくなる
        cookieUtils.removeCookiesOnLegacyDomain('CognitoIdentityServiceProvider', LEGACY_COOKIE_DOMAIN);
        const expayload = extractCognitoPayload(authenticatedUser);
        dispatch(
          signIn({
            authState: AuthState.SignedIn,
            user: expayload,
          }),
        );
        pingCompanyUserActiveLog(expayload['cognito:username']);
      }
    });
  }, [dispatch]);

  return isloading ? (
    <ErrorBoundaryWrapper>
      <Loading />
    </ErrorBoundaryWrapper>
  ) : (
    <ErrorBoundaryWrapper>
      <RouteWrapper>
        <React.Suspense fallback={<Loading />}>
          <Shell>
            <Switch>
              <Route
                exact
                path="/"
                render={() => {
                  return userState.authState === AuthState.SignedIn && userState.user ? (
                    <Redirect to="/top" />
                  ) : (
                    <Redirect to="/signin" />
                  );
                }}
              />
              <Route
                exact
                path="/signin"
                render={() => {
                  return userState.authState === AuthState.SignedIn && userState.user ? (
                    <Redirect to="/top" />
                  ) : (
                    <Signin />
                  );
                }}
              />
              <PrivateRoute exact path="/top" render={() => <Top />} userState={userState} />
              <PrivateRoute
                exact
                path="/learners"
                render={() => <Learners />}
                userState={userState}
              />
              <PrivateRoute
                exact
                path="/learner/:learnerId"
                render={() => <Learner />}
                userState={userState}
              />
              <PrivateRoute
                exact
                path="/lessons"
                render={() => <Lessons />}
                userState={userState}
              />
              <PrivateRoute
                exact
                path="/lesson/:companyId/:lessonId"
                render={() => <Lesson />}
                userState={userState}
              />
              <PrivateRoute
                exact
                path="/notices"
                render={() => <Notices />}
                userState={userState}
              />
              {/* NOTE 一旦非表示 */}
              <PrivateRoute
                exact
                path="/agreement"
                render={() => <Agreement />}
                userState={userState}
              />
              <PrivateRoute
                exact
                path="/contact"
                render={() => <Contact />}
                userState={userState}
              />
              {/* <PrivateRoute
              exact
              path="/lesson_notes"
              render={() => <LessonNotes />}
              userState={userState}
            /> */}
              <PrivateRoute
                exact
                path="/feedback"
                render={() => <Feedback />}
                userState={userState}
              />
              <Route
                exact
                path="*"
                render={() => {
                  return (
                    <ErrorPage
                      errorCode={ERROR_CODE.HTTP_404}
                      error={
                        new Error(ERROR_MESSAGE.HTTP_404.ja + ` url:${document.location.href}`)
                      }
                      message={ERROR_MESSAGE.HTTP_404.ja}
                    />
                  );
                }}
              />
            </Switch>
          </Shell>
        </React.Suspense>
      </RouteWrapper>
    </ErrorBoundaryWrapper>
  );
};
export default App;

export const exchangeTimeZone = (datedime: Date) => {
  // ローカル時間に変更
  let localDatetime = datedime.toLocaleString();
  // 出力用にフォーマット
  let fromattedTime = format(Date.parse(localDatetime), 'yyyy/MM/dd HH:mm:ss');
  return fromattedTime;
};
